// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { useNavigate, useLocation } from 'react-router-dom';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import ImageExtension from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Highlight from '@tiptap/extension-highlight';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { API_ENDPOINTS } from '../config/api';
import { Node, mergeAttributes, Extension } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import './TipTapEditor.css';

const VIDEO_ASSET_REGEX = /\.(mp4|webm|ogg|mov|m4v|m3u8)(\?|#|$)/i;

const isVideoAssetEntry = (asset = {}) => {
  const url = (asset.url || asset.src || '').toString();
  const fileName = (asset.fileName || '').toString();
  const mimeType = (asset.mimeType || asset.contentType || '').toString();
  const mediaType = (asset.mediaType || '').toString();

  if (/^video\//i.test(mimeType)) return true;
  if (/video/i.test(mediaType)) return true;
  return VIDEO_ASSET_REGEX.test(url) || VIDEO_ASSET_REGEX.test(fileName);
};

const extractQuoteAndAuthor = (rawText = '') => {
  const text = String(rawText || '').replace(/\s+/g, ' ').trim();
  if (!text) return { quoteText: '', author: '' };

  const patterns = [
    /^(.*?)(?:\s*-\s*by\s+)([A-Za-z][\w .,'"-]{1,80})\s*$/i,
    /^(.*?)(?:\s*-\s+)([A-Za-z][\w .,'"-]{1,80})\s*$/,
    /^(.*?)(?:\s+by\s+)([A-Za-z][\w .,'"-]{1,80})\s*$/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match && match[1] && match[2]) {
      const quoteText = match[1].trim();
      const author = match[2].trim();
      if (quoteText && author) {
        return { quoteText, author };
      }
    }
  }

  return { quoteText: text, author: '' };
};


// Custom Image extension with title support
const ImageWithTitle = ImageExtension.extend({
  addAttributes() {
    const parentAttrs = this.parent ? this.parent() : {};
    return {
      ...parentAttrs,
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      'data-filename': {
        default: null,
      },
      'data-processed': {
        default: null,
      },
    };
  },
});

// Custom Instagram Embed Node View Component
const InstagramEmbedComponent = ({ node, deleteNode, updateAttributes }) => {
  const handleEdit = () => {
    const currentUrl = node.attrs.url;
    const url = prompt('Enter Instagram Post URL:', currentUrl);
    
    if (url !== null) {
      if (url) {
        updateAttributes({ url });
      } else {
        deleteNode();
      }
    }
  };

  return (
    <NodeViewWrapper className="instagram-embed-node-view">
      <div className="instagram-embed-icon">
        <i className="fi fi-brands-instagram"></i>
      </div>
      <div 
        className="instagram-embed-content"
        onClick={handleEdit}
        title="Click to edit URL"
      >
        <div className="instagram-embed-title">Instagram Post</div>
        <div className="instagram-embed-url">{node.attrs.url}</div>
      </div>
      <button 
        className="instagram-embed-delete"
        onClick={deleteNode}
        title="Remove Instagram embed"
        type="button"
      >
        <i className="fi fi-rr-cross-small"></i>
      </button>
    </NodeViewWrapper>
  );
};

// Custom Instagram Embed Extension
const InstagramEmbed = Node.create({
  name: 'instagramEmbed',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      url: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-instagram-url]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-instagram-url': HTMLAttributes.url })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InstagramEmbedComponent);
  },
});

// Custom Twitter Embed Node View Component
const TwitterEmbedComponent = ({ node, deleteNode, updateAttributes }) => {
  const [isHovered, setIsHovered] = useState(false); // eslint-disable-line no-unused-vars
  
  const handleEdit = () => {
    const currentUrl = node.attrs.url;
    const url = prompt('Enter Twitter Post URL:', currentUrl);
    
    if (url !== null) {
      if (url) {
        updateAttributes({ url });
      } else {
        // Empty URL means delete
        deleteNode();
      }
    }
  };

  return (
    <NodeViewWrapper className="twitter-embed-node-view">
      <div className="twitter-embed-icon">
        <i className="fi fi-brands-twitter"></i>
      </div>
      <div 
        className="twitter-embed-content"
        onClick={handleEdit}
        title="Click to edit URL"
      >
        <div className="twitter-embed-title">Twitter Post</div>
        <div className="twitter-embed-url">{node.attrs.url}</div>
      </div>
      <button 
        className="twitter-embed-delete"
        onClick={deleteNode}
        title="Remove Twitter embed"
        type="button"
      >
        <i className="fi fi-rr-cross-small"></i>
      </button>
    </NodeViewWrapper>
  );
};

// Custom Twitter Embed Extension
const TwitterEmbed = Node.create({
  name: 'twitterEmbed',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      url: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-twitter-url]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-twitter-url': HTMLAttributes.url })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TwitterEmbedComponent);
  },
});

// Custom YouTube Embed Node View Component
const YouTubeEmbedComponent = ({ node, deleteNode, updateAttributes }) => {
  const handleEdit = () => {
    const currentId = node.attrs.videoId;
    const url = prompt('Enter YouTube Video URL or ID:', currentId);
    
    if (url !== null) {
      if (url) {
        // Extract video ID if full URL is pasted
        let videoId = url;
        try {
            if (url.includes('youtube.com') || url.includes('youtu.be')) {
                const urlObj = new URL(url);
                if (urlObj.hostname.includes('youtu.be')) {
                    videoId = urlObj.pathname.slice(1);
                } else {
                    videoId = urlObj.searchParams.get('v');
                }
            }
        } catch (e) {
            console.error('Error parsing YouTube URL:', e);
        }
        updateAttributes({ videoId });
      } else {
        deleteNode(); // Empty input means delete
      }
    }
  };

  return (
    <NodeViewWrapper className="youtube-embed-node-view">
      <div className="youtube-embed-icon">
        <i className="fi fi-brands-youtube"></i>
      </div>
      <div 
        className="youtube-embed-content"
        onClick={handleEdit}
        title="Click to edit Video ID"
      >
        <div className="youtube-embed-title">YouTube Video</div>
        <div className="youtube-embed-url">{node.attrs.videoId}</div>
      </div>
      <button 
        className="youtube-embed-delete"
        onClick={deleteNode}
        title="Remove YouTube embed"
        type="button"
      >
        <i className="fi fi-rr-cross-small"></i>
      </button>
    </NodeViewWrapper>
  );
};

// Custom YouTube Embed Extension
const YouTubeEmbed = Node.create({
  name: 'youtubeEmbed',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      videoId: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-youtube-video-id]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-youtube-video-id': HTMLAttributes.videoId })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(YouTubeEmbedComponent);
  },
});

const VideoAssetComponent = ({ node, deleteNode, updateAttributes }) => {
  const handleEdit = () => {
    const currentSrc = node.attrs.src;
    const url = prompt('Enter video URL:', currentSrc);
    if (url !== null) {
      if (url) {
        updateAttributes({ src: url });
      } else {
        deleteNode();
      }
    }
  };

  return (
    <NodeViewWrapper className="youtube-embed-node-view">
      <div className="youtube-embed-icon">
        <i className="fi fi-rr-play-alt"></i>
      </div>
      <div className="youtube-embed-content" onClick={handleEdit} title="Click to edit video URL">
        <div className="youtube-embed-title">Video Asset</div>
        <div className="youtube-embed-url">{node.attrs.title || node.attrs['data-filename'] || node.attrs.src}</div>
      </div>
      <button className="youtube-embed-delete" onClick={deleteNode} title="Remove video" type="button">
        <i className="fi fi-rr-cross-small"></i>
      </button>
    </NodeViewWrapper>
  );
};

const VideoAsset = Node.create({
  name: 'videoAsset',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: { default: '' },
      title: { default: '' },
      poster: { default: '' },
      'data-filename': { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-video-asset-src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-video-asset-src': HTMLAttributes.src,
      'data-video-asset-title': HTMLAttributes.title,
      'data-video-asset-poster': HTMLAttributes.poster,
      'data-filename': HTMLAttributes['data-filename'],
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoAssetComponent);
  },
});

// Custom Blog Embed Node View Component
const BlogEmbedComponent = ({ node, deleteNode, updateAttributes }) => {
  const handleEdit = () => {
    const currentUrl = node.attrs.url;
    const currentTitle = node.attrs.title;

    const url = prompt('Enter Blog URL:', currentUrl);
    if (url === null) return;

    if (!url) {
      deleteNode();
      return;
    }

    const title = prompt('Enter Blog Title:', currentTitle);
    updateAttributes({ url, title });
  };

  return (
    <NodeViewWrapper className="blog-embed-node-view" as="span">
      <span className="blog-embed-icon-wrapper">
         <i className="fi fi-sr-journal-alt"></i>
      </span>
      <span className="blog-embed-content">
        <span className="blog-embed-title-text">{node.attrs.title || 'Blog Post'}</span>
      </span>
      <span className="blog-embed-actions-group">
        <button 
            type="button" 
            className="blog-embed-action-btn edit-btn"
            onClick={handleEdit}
            title="Edit Link"
        >
             <i className="fi fi-rr-pencil"></i>
        </button>
        <button 
            type="button" 
            className="blog-embed-action-btn delete-btn" 
            onClick={deleteNode}
            title="Remove"
        >
            <i className="fi fi-rr-cross-small"></i>
        </button>
      </span>
    </NodeViewWrapper>
  );
};

// Custom Blog Embed Extension
const BlogEmbed = Node.create({
  name: 'blogEmbed',

  group: 'inline', // Changed from block to inline
  inline: true,    // Explicitly set inline

  atom: true,

  addAttributes() {
    return {
      url: {
        default: '',
      },
      title: {
        default: '',
      },
      image: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-blog-url]', // Changed div to span
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { // Changed div to span
      'data-blog-url': HTMLAttributes.url,
      'data-blog-title': HTMLAttributes.title,
      'data-blog-image': HTMLAttributes.image,
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(BlogEmbedComponent);
  },
});

// Custom Podcast Link Node View Component
const PodcastLinkComponent = ({ node, deleteNode, updateAttributes }) => {
  const handleEdit = () => {
    const currentUrl = node.attrs.url;
    const url = prompt('Enter Podcast URL:', currentUrl);
    
    if (url !== null) {
      if (url) {
        updateAttributes({ url });
      } else {
        deleteNode();
      }
    }
  };

  return (
    <NodeViewWrapper className="podcast-link-node-view">
      <a href={node.attrs.url} target="_blank" rel="noopener noreferrer" className="podcast-link-chip" onClick={(e) => e.preventDefault()}>
        <span className="podcast-icon">
             <i className="fi fi-sr-podcast"></i>
        </span>
        <span className="podcast-text">{node.attrs.text}</span>
      </a>
      <div className="podcast-link-actions">
        <button 
            type="button" 
            className="podcast-action-btn edit-btn"
            onClick={handleEdit}
            title="Edit Link"
        >
             <i className="fi fi-rr-pencil"></i>
        </button>
        <button 
            type="button" 
            className="podcast-action-btn delete-btn" 
            onClick={deleteNode}
            title="Remove Link"
        >
            <i className="fi fi-rr-cross-small"></i>
        </button>
      </div>
    </NodeViewWrapper>
  );
};

// Custom Podcast Link Extension
const PodcastLink = Node.create({
  name: 'podcastLink',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      url: {
        default: '',
      },
      text: {
        default: '',
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-podcast-link]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-podcast-link': '', 'data-url': HTMLAttributes.url, 'data-text': HTMLAttributes.text })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PodcastLinkComponent);
  },
});

// Custom Blog Link Node View Component
const BlogLinkComponent = ({ node, deleteNode, updateAttributes }) => {
  const handleEdit = () => {
    const currentUrl = node.attrs.url;
    const url = prompt('Enter Blog URL:', currentUrl);
    
    if (url !== null) {
      if (url) {
        updateAttributes({ url });
      } else {
        deleteNode();
      }
    }
  };

  return (
    <NodeViewWrapper className="blog-link-node-view">
      <a href={node.attrs.url} target="_blank" rel="noopener noreferrer" className="blog-link-chip" onClick={(e) => e.preventDefault()}>
        <span className="blog-icon">
             <i className="fi fi-sr-journal-alt"></i>
        </span>
        <span className="blog-text">{node.attrs.text}</span>
      </a>
      <div className="blog-link-actions">
        <button 
            type="button" 
            className="blog-action-btn edit-btn"
            onClick={handleEdit}
            title="Edit Link"
        >
             <i className="fi fi-rr-pencil"></i>
        </button>
        <button 
            type="button" 
            className="blog-action-btn delete-btn" 
            onClick={deleteNode}
            title="Remove Link"
        >
            <i className="fi fi-rr-cross-small"></i>
        </button>
      </div>
    </NodeViewWrapper>
  );
};

// Custom Blog Link Extension
const BlogLink = Node.create({
  name: 'blogLink',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      url: {
        default: '',
      },
      text: {
        default: '',
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-blog-link]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-blog-link': '', 'data-url': HTMLAttributes.url, 'data-text': HTMLAttributes.text })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(BlogLinkComponent);
  },
});



// eslint-disable-next-line no-unused-vars
function ImageProcessor({ editor }) {
  const isProcessingRef = useRef(false);
  const processedImagesRef = useRef(new Set());
  const lastProcessedContentRef = useRef('');
  const processingTimeoutRef = useRef(null);

  useEffect(() => {
    if (!editor) return;

    const processedImagesRefCopy = processedImagesRef.current;

    const processImages = () => {
      // Prevent infinite loops - check multiple conditions
      if (isProcessingRef.current) {
        return;
      }
      
      const editorElement = document.querySelector('.ProseMirror');
      if (!editorElement) return;

      // Get current content hash to detect if content actually changed
      // Use a more stable hash that excludes our added elements
      // Also normalize whitespace to prevent false positives
      const stableContent = editorElement.innerHTML
        .replace(/data-processed="true"/g, '')
        .replace(/<div class="image-wrapper[^"]*">/g, '')
        .replace(/<\/div>/g, '')
        .replace(/<button class="image-delete-btn[^"]*">.*?<\/button>/gs, '')
        .replace(/\s+/g, ' ')
        .trim();
      
      if (stableContent === lastProcessedContentRef.current) {
        return; // Content hasn't changed, skip processing
      }

      // Find all image nodes that haven't been processed
      const images = Array.from(editorElement.querySelectorAll('img:not(.image-carousel-image)'))
        .filter(img => {
          // Add null checks
          if (!img || !img.src) return false;
          // Check if image already has a processed marker
          if (img.dataset.processed === 'true') return false;
          // Check if image is already wrapped with delete button
          const wrapper = img.closest('.image-wrapper');
          if (wrapper && wrapper.querySelector('.image-delete-btn')) return false;
          const imgId = (img.src || '') + (img.getAttribute('title') || img.getAttribute('alt') || '');
          return imgId && !processedImagesRef.current.has(imgId);
        });

      if (images.length === 0) {
        lastProcessedContentRef.current = stableContent;
        return;
      }

      isProcessingRef.current = true;

      try {
        // Group consecutive images
        const groups = [];
        let currentGroup = [];

        images.forEach((img, idx) => {
          // Add null checks
          if (!img || !img.src) return;
          
          const imgNode = img.closest('p') || img.parentElement;
          if (!imgNode) return;
          
          const nextImg = images[idx + 1];
          const nextImgNode = nextImg ? (nextImg.closest('p') || nextImg.parentElement) : null;

          const src = img.src || '';
          const title = img.getAttribute('title') || img.getAttribute('alt') || '';

          currentGroup.push({
            img,
            imgNode,
            src: src,
            title: title
          });

          // Check if next image is consecutive
          if (!nextImg || !isConsecutive(imgNode, nextImgNode)) {
            if (currentGroup.length > 1) {
              groups.push([...currentGroup]);
            }
            currentGroup = [];
          }
        });

        // Create carousels for groups
        groups.forEach((group) => {
          const firstNode = group[0].imgNode;
          if (!firstNode || !firstNode.parentNode) return;

          // Mark images as processed before DOM manipulation
          group.forEach(({ img, src, title }) => {
            const imgId = src + title;
            processedImagesRef.current.add(imgId);
          });

          const mount = document.createElement('div');
          mount.className = 'image-carousel';
          mount.setAttribute('data-processed', 'true');
          firstNode.parentNode.insertBefore(mount, firstNode);

          // Remove original image nodes
          group.forEach(({ imgNode }) => {
            if (imgNode && imgNode.parentNode) {
              imgNode.parentNode.removeChild(imgNode);
            }
          });

          // Create carousel structure
          const items = group.map(({ src, title }) => ({ src, title }));
          let currentIndex = 0;

          const frame = document.createElement('div');
          frame.className = 'image-carousel-frame';
          const img = document.createElement('img');
          img.className = 'image-carousel-image';
          img.src = items[0].src;
          img.alt = items[0].title || '';
          frame.appendChild(img);

          const controls = document.createElement('div');
          controls.className = 'image-carousel-controls';
          const desc = document.createElement('div');
          desc.className = 'image-carousel-desc';
          desc.textContent = items[0].title || '';
          const nav = document.createElement('div');
          nav.className = 'image-carousel-nav';

          const updateCarousel = () => {
            img.src = items[currentIndex].src;
            img.alt = items[currentIndex].title || '';
            desc.textContent = items[currentIndex].title || '';
            counter.textContent = `${currentIndex + 1} / ${items.length}`;
          };

          const prevBtn = document.createElement('button');
          prevBtn.className = 'image-carousel-btn';
          prevBtn.innerHTML = '<i class="fi fi-rr-caret-left"></i>';
          prevBtn.onclick = () => {
            currentIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
            updateCarousel();
          };

          const counter = document.createElement('span');
          counter.className = 'image-carousel-counter';
          counter.textContent = `1 / ${items.length}`;

          const nextBtn = document.createElement('button');
          nextBtn.className = 'image-carousel-btn';
          nextBtn.innerHTML = '<i class="fi fi-rr-caret-right"></i>';
          nextBtn.onclick = () => {
            currentIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
            updateCarousel();
          };

          nav.appendChild(prevBtn);
          nav.appendChild(counter);
          nav.appendChild(nextBtn);
          controls.appendChild(desc);
          controls.appendChild(nav);

          mount.appendChild(frame);
          mount.appendChild(controls);
        });

        // Add titles and delete buttons to single images (not in carousels)
        images.forEach(img => {
          // Add null checks
          if (!img || !img.src) return;
          if (img.closest('.image-carousel')) return;
          // Skip if already marked as processed
          if (img.dataset.processed === 'true') return;
          
          const imgId = (img.src || '') + (img.getAttribute('title') || img.getAttribute('alt') || '');
          if (!imgId || processedImagesRef.current.has(imgId)) return;
          
          // Mark as processed BEFORE DOM manipulation to prevent re-processing
          img.dataset.processed = 'true';
          processedImagesRef.current.add(imgId);
          
          // eslint-disable-next-line no-unused-vars
          const title = img.getAttribute('title') || img.getAttribute('alt') || '';
          
          // Wrap image in a container for delete button
          const imgParent = img.closest('p') || img.parentElement;
          if (!imgParent) return;
          
          // Check if already wrapped - look for wrapper in parent
          let imageWrapper = img.parentElement;
          const isWrapped = imageWrapper.classList && imageWrapper.classList.contains('image-wrapper');
          
          if (!isWrapped) {
            // Create wrapper
            imageWrapper = document.createElement('div');
            imageWrapper.className = 'image-wrapper';
            imageWrapper.dataset.processed = 'true';
            imageWrapper.style.cssText = `
              position: relative !important;
              display: inline-block !important;
              max-width: 100% !important;
              margin: 0.5em 0 !important;
            `;
            
            // Insert wrapper before image and move image into wrapper
            imgParent.insertBefore(imageWrapper, img);
            imageWrapper.appendChild(img);
          }
          
          // Get filename from data attribute or title/alt
          const fileName = img.getAttribute('data-filename') || img.getAttribute('title') || img.getAttribute('alt') || '';
          
          // Add filename label with close button if it doesn't exist and we have a filename
          if (fileName && !imageWrapper.querySelector('.image-filename-label')) {
            const filenameContainer = document.createElement('div');
            filenameContainer.className = 'image-filename-label';
            filenameContainer.style.cssText = `
              position: absolute;
              bottom: 8px;
              left: 8px;
              right: 8px;
              background-color: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 6px 8px;
              border-radius: 4px;
              font-size: 12px;
              z-index: 5;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
            `;
            
            const filenameText = document.createElement('span');
            filenameText.textContent = fileName;
            filenameText.style.cssText = `
              flex: 1;
              word-break: break-word;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `;
            
            const filenameCloseBtn = document.createElement('button');
            filenameCloseBtn.innerHTML = '<i class="fi fi-rr-cross-small"></i>';
            filenameCloseBtn.title = 'Remove filename';
            filenameCloseBtn.setAttribute('aria-label', 'Remove filename');
            filenameCloseBtn.style.cssText = `
              background: none;
              border: none;
              color: white;
              cursor: pointer;
              padding: 2px 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              opacity: 0.8;
              transition: opacity 0.2s;
              flex-shrink: 0;
            `;
            filenameCloseBtn.onmouseenter = () => {
              filenameCloseBtn.style.opacity = '1';
            };
            filenameCloseBtn.onmouseleave = () => {
              filenameCloseBtn.style.opacity = '0.8';
            };
            filenameCloseBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              // Remove filename from image
              img.removeAttribute('data-filename');
              if (filenameContainer.parentNode) {
                filenameContainer.parentNode.removeChild(filenameContainer);
              }
            };
            
            filenameContainer.appendChild(filenameText);
            filenameContainer.appendChild(filenameCloseBtn);
            imageWrapper.appendChild(filenameContainer);
          }
          
          // Add delete button if it doesn't exist
          if (!imageWrapper.querySelector('.image-delete-btn')) {
            const deleteBtn = document.createElement('button');
            deleteBtn.className = 'image-delete-btn';
            deleteBtn.type = 'button';
            deleteBtn.innerHTML = '<i class="fi fi-rr-cross-small"></i>';
            deleteBtn.title = 'Delete image';
            deleteBtn.setAttribute('aria-label', 'Delete image');
            // Add inline styles to ensure visibility
            deleteBtn.style.cssText = `
              position: absolute;
              top: 8px;
              right: 8px;
              width: 28px;
              height: 28px;
              background-color: rgba(220, 38, 38, 0.9);
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 14px;
              z-index: 10;
              opacity: 0.8;
              transition: opacity 0.2s;
              padding: 0;
              margin: 0;
            `;
            deleteBtn.onmouseenter = () => {
              deleteBtn.style.opacity = '1';
              deleteBtn.style.backgroundColor = 'rgba(220, 38, 38, 1)';
            };
            deleteBtn.onmouseleave = () => {
              deleteBtn.style.opacity = '0.8';
              deleteBtn.style.backgroundColor = 'rgba(220, 38, 38, 0.9)';
            };
            deleteBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              // Find the image node in TipTap and delete it
              const imgNode = imageWrapper.querySelector('img');
              if (imgNode && editor) {
                try {
                  // Find the position of the image in the editor
                  const pos = editor.view.posAtDOM(imgNode, -1);
                  if (pos !== null && pos !== undefined && pos >= 0) {
                    // Get the node at this position
                    const resolvedPos = editor.state.doc.resolve(pos);
                    const node = resolvedPos.node();
                    if (node && node.type.name === 'image') {
                      // Delete the image node
                      editor.chain().focus().setTextSelection(pos).deleteSelection().run();
                    } else {
                      // Try to find image in parent
                      const parent = resolvedPos.parent;
                      if (parent) {
                        let imagePos = null;
                        parent.forEach((child, offset) => {
                          if (child.type.name === 'image') {
                            imagePos = resolvedPos.start() + offset;
                          }
                        });
                        if (imagePos !== null) {
                          editor.chain().focus().setTextSelection(imagePos).deleteSelection().run();
                        } else {
                          // Fallback: remove from DOM and trigger update
                          if (imageWrapper.parentNode) {
                            imageWrapper.parentNode.removeChild(imageWrapper);
                            editor.view.dispatch(editor.state.tr);
                          }
                        }
                      }
                    }
                  } else {
                    // Fallback: remove from DOM
                    if (imageWrapper.parentNode) {
                      imageWrapper.parentNode.removeChild(imageWrapper);
                      // Trigger editor update
                      setTimeout(() => {
                        if (editor && editor.view) {
                          editor.view.dispatch(editor.state.tr);
                        }
                      }, 100);
                    }
                  }
                } catch (error) {
                  console.error('Error deleting image:', error);
                  // Fallback: remove from DOM
                  if (imageWrapper.parentNode) {
                    imageWrapper.parentNode.removeChild(imageWrapper);
                  }
                }
              }
            };
            imageWrapper.appendChild(deleteBtn);
          }
          
          // Title display removed - filename is shown in overlay instead
          // No need to add title as separate DOM element
        });
        // Update last processed content (using stable hash)
        // Normalize again to ensure consistency
        const normalizedContent = editorElement.innerHTML
          .replace(/data-processed="true"/g, '')
          .replace(/<div class="image-wrapper[^"]*">/g, '')
          .replace(/<\/div>/g, '')
          .replace(/<button class="image-delete-btn[^"]*">.*?<\/button>/gs, '')
          .replace(/\s+/g, ' ')
          .trim();
        lastProcessedContentRef.current = normalizedContent;
      } finally {
        isProcessingRef.current = false;
      }
    };

    const isConsecutive = (node1, node2) => {
      if (!node1 || !node2) return false;
      return node1.nextSibling === node2 ||
        (node1.nextSibling && node1.nextSibling.nextSibling === node2);
    };

    // Process images when editor updates, but debounce to prevent loops
    // IMPORTANT: Only process images, don't trigger editor updates
    const handleUpdate = ({ editor: editorInstance, transaction }) => {
      // Skip if we're already processing to prevent loops
      if (isProcessingRef.current) {
        return;
      }
      
      // Skip if this is an internal update (marked with preventUpdate meta)
      if (transaction && transaction.getMeta('preventUpdate')) {
        return;
      }
      
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      // Use longer timeout to prevent rapid-fire updates
      // Only process if we're not currently processing
      processingTimeoutRef.current = setTimeout(() => {
        if (!isProcessingRef.current) {
          processImages();
        }
      }, 1000);
    };

    // Handle transaction events (when content is set programmatically)
    const handleTransaction = ({ transaction }) => {
      // Only process if this is a content-setting transaction, not our own updates
      if (transaction.getMeta('preventUpdate') || transaction.getMeta('addToHistory') === false) {
        return; // Skip if this is our own update
      }
      
      // Skip if we're already processing
      if (isProcessingRef.current) {
        return;
      }
      
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      // Clear processed markers when content is set programmatically (like after save)
      const editorElement = document.querySelector('.ProseMirror');
      if (editorElement) {
        // Remove processed markers from all images
        editorElement.querySelectorAll('img[data-processed="true"]').forEach(img => {
          img.removeAttribute('data-processed');
        });
        editorElement.querySelectorAll('.image-wrapper[data-processed="true"]').forEach(wrapper => {
          wrapper.removeAttribute('data-processed');
        });
        // Clear the processed set
        processedImagesRef.current.clear();
        lastProcessedContentRef.current = '';
      }
      processingTimeoutRef.current = setTimeout(() => {
        if (!isProcessingRef.current) {
          processImages();
        }
      }, 1000);
    };

    editor.on('update', handleUpdate);
    editor.on('transaction', handleTransaction);

    // Initial processing with longer delay to ensure DOM is ready
    const initialTimeout = setTimeout(() => {
      if (!isProcessingRef.current) {
        processedImagesRef.current.clear();
        processImages();
      }
    }, 1000);

    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (initialTimeout) {
        clearTimeout(initialTimeout);
      }
      editor.off('update', handleUpdate);
      editor.off('transaction', handleTransaction);
      isProcessingRef.current = false;
      processedImagesRefCopy.clear();
    };
  }, [editor]);

  return null;
}

// Image Asset Dialog Component
function ImageAssetDialog({ isOpen, onClose, onSelectImages, editor, navigate, location, onBeforeNavigate = null }) {
  const [mode, setMode] = useState(null); // 'existing' or 'new'
  const [imageAssets, setImageAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(new Set());


  const fetchImageAssets = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(API_ENDPOINTS.ADMIN.DATABASE_COLLECTION('ImageAssets'), {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: "include",
        cache: "no-store"
      });

      if (res.ok) {
        const rawText = await res.text();
        const trimmed = rawText ? rawText.trim() : '';
        if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return;
        let json;
        try { json = JSON.parse(trimmed); } catch (e) { return; }
        setImageAssets(Array.isArray(json.data) ? json.data : []);
      }
    } catch (error) {
      console.error('Error fetching ImageAssets:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const fetchImageAsset = async (id) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(API_ENDPOINTS.ADMIN.COLLECTION_GET('ImageAssets', id), {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: "include",
        cache: "no-store"
      });

      if (res.ok) {
        const rawText = await res.text();
        const trimmed = rawText ? rawText.trim() : '';
        if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return null;
        let json;
        try { json = JSON.parse(trimmed); } catch (e) { return null; }
        return json.data || json;
      }
    } catch (error) {
      console.error('Error fetching ImageAsset:', error);
    }
    return null;
  };

  useEffect(() => {
    if (isOpen && mode === 'existing') {
      fetchImageAssets();
    }
  }, [isOpen, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleSelect = (assetId) => {
    const newSelected = new Set(selectedAssets);
    if (newSelected.has(assetId)) {
      newSelected.delete(assetId);
    } else {
      newSelected.add(assetId);
    }
    setSelectedAssets(newSelected);
  };

  const handleAddSelected = () => {
    // Iterate over selectedAssets (a Set) which preserves insertion order (click order)
    const selected = [...selectedAssets]
      .map(id => imageAssets.find(asset => String(asset._id || asset.id) === id))
      .filter(Boolean);
    if (selected.length > 0) {
      onSelectImages(selected);
      onClose();
      setMode(null);
      setSelectedAssets(new Set());
    }
  };

  const handleAddNew = () => {
    // Save parent form state before navigating away
    if (onBeforeNavigate) onBeforeNavigate();

    // Store current location to return to
    const returnPath = location.pathname + location.search;
    sessionStorage.setItem('imageAssetReturnPath', returnPath);
    // Navigate to create ImageAssets page
    navigate(`/admin/database/blogdb/ImageAssets/create?returnToEditor=true`);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }} onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
        setMode(null);
        setSelectedAssets(new Set());
      }
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#0f172a' }}>Add Image or Video</h2>
          <button
            onClick={() => {
              onClose();
              setMode(null);
              setSelectedAssets(new Set());
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px 8px',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {!mode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              onClick={() => setMode('existing')}
              style={{
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 500,
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#d1d5db';
              }}
            >
              Add Existing Asset
            </button>
            <button
              onClick={handleAddNew}
              style={{
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 500,
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#d1d5db';
              }}
            >
              Add New Asset
            </button>
          </div>
        ) : mode === 'existing' ? (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
            ) : imageAssets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                No assets found.
              </div>
            ) : (
              <>
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                  {imageAssets.map((asset) => {
                    const assetId = String(asset._id || asset.id);
                    const isSelected = selectedAssets.has(assetId);
                    return (
                      <div
                        key={assetId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          backgroundColor: isSelected ? '#eff6ff' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => handleToggleSelect(assetId)}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(assetId)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        {asset.url && (isVideoAssetEntry(asset) ? (
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: '#111827',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px'
                          }}>
                            <i className="fi fi-rr-play-alt"></i>
                          </div>
                        ) : (
                          <img
                            src={asset.url}
                            alt={asset.altText || asset.fileName || ''}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #e5e7eb'
                            }}
                          />
                        ))}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                            {asset.fileName || 'Untitled'}
                            {isVideoAssetEntry(asset) ? ' (Video)' : ' (Image)'}
                          </div>
                          {asset.title && (
                            <div style={{ fontSize: '14px', color: '#6b7280' }}>
                              {asset.title}
                            </div>
                          )}
                          {asset.url && (
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', wordBreak: 'break-all' }}>
                              {asset.url.substring(0, 60)}...
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setMode(null);
                      setSelectedAssets(new Set());
                    }}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddSelected}
                    disabled={selectedAssets.size === 0}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: selectedAssets.size === 0 ? '#d1d5db' : '#059669',
                      color: 'white',
                      cursor: selectedAssets.size === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    Add ({selectedAssets.size})
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Extension: Tab key moves focus to the next/previous form field (instead of being captured by ProseMirror)
const TabNavigationExtension = Extension.create({
  name: 'tabNavigation',
  addKeyboardShortcuts() {
    const moveFocus = (direction) => {
      const editorDom = this.editor.view.dom;
      const focusableSelectors = [
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
      ].join(', ');
      const allFocusable = Array.from(document.querySelectorAll(focusableSelectors))
        .filter(el => el.offsetParent !== null);
      const editorIndex = allFocusable.findIndex(el => el === editorDom || editorDom.contains(el));
      if (direction === 'next' && editorIndex !== -1 && editorIndex + 1 < allFocusable.length) {
        allFocusable[editorIndex + 1].focus();
      } else if (direction === 'prev' && editorIndex > 0) {
        allFocusable[editorIndex - 1].focus();
      }
    };
    return {
      Tab: () => {
        // If cursor is inside a list item, let StarterKit handle indentation
        if (this.editor.isActive('listItem')) return false;
        this.editor.commands.blur();
        moveFocus('next');
        return true;
      },
      'Shift-Tab': () => {
        if (this.editor.isActive('listItem')) return false;
        this.editor.commands.blur();
        moveFocus('prev');
        return true;
      },
    };
  },
});

export default function TipTapEditor({ value, onChange, isBlogContent = false, readOnly = false, onBeforeNavigate = null, navPinned = false }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Handle fullscreen mode - prevent body scroll
  useEffect(() => {
    if (isFullscreen) {
      // Prevent body scroll when in fullscreen
      document.body.classList.add('tiptap-fullscreen-active');
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when exiting fullscreen
      document.body.classList.remove('tiptap-fullscreen-active');
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('tiptap-fullscreen-active');
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  
  // Initialize editor only once - decouple from React state
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Link.configure({
        openOnClick: false,
      }),
      ImageWithTitle,
      TwitterEmbed,
      InstagramEmbed,
      YouTubeEmbed,
      VideoAsset,
      BlogEmbed,
      PodcastLink,
      BlogLink,
      Underline,
      Subscript,
      Superscript,
      Highlight,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TabNavigationExtension,
    ],
    content: (() => {
      // Initialize with value only once
      if (typeof value === 'string' && value.trim()) {
        return value;
      } else if (typeof value === 'object' && value && value.type === 'doc') {
        return value;
      }
      return { type: 'doc', content: [] };
    })(),
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      // Only collect updated data, don't set it back immediately
      // This prevents the infinite loop
      if (!editor) {
        console.warn('TipTap editor is null in onUpdate');
        return;
      }
      
      if (readOnly) return; // Don't allow updates in read-only mode
      
      try {
        const json = editor.getJSON();
        const html = editor.getHTML();
        if (!json) {
          console.warn('TipTap editor.getJSON() returned null');
          onChange({ json: { type: 'doc', content: [] }, html: '' });
          return;
        }
        // Return both formats so the parent can decide which to use/store
        onChange({ json, html });
      } catch (error) {
        console.error('Error in TipTap onUpdate:', error);
        // Provide a fallback empty document
        onChange({ json: { type: 'doc', content: [] }, html: '' });
      }
    },
  }, [readOnly]); // Include readOnly in dependencies



  // Only set content once when editor is first created (if initial data exists)
  useEffect(() => {
    if (editor && value && !editor.isDestroyed) {
      // Only set if editor content is empty or different from initial value
      const currentContent = editor.getJSON();
      const valueStr = typeof value === 'string' ? value : JSON.stringify(value);
      const currentStr = JSON.stringify(currentContent);
      
      if (valueStr !== currentStr) {
        let effectiveValue = value;
        if (typeof value === 'string' && value.trim()) {
          effectiveValue = value;
        } else if (typeof value === 'object' && value && value.type === 'doc') {
          effectiveValue = value;
        } else {
          effectiveValue = { type: 'doc', content: [] };
        }
        
        editor.commands.setContent(effectiveValue, false);
      }
    }
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if returning from creating new asset - MUST be before early return
  useEffect(() => {
    if (!editor || !isBlogContent) return;
    
    const urlParams = new URLSearchParams(location.search);
    const returnFromCreate = urlParams.get('returnFromCreate');
    const newAssetId = urlParams.get('newAssetId');
    
    if (returnFromCreate === 'true' && newAssetId) {
      // Fetch the newly created asset and add it
      const fetchAndAddAsset = async () => {
        try {
          const token = sessionStorage.getItem("token");
          const res = await fetch(API_ENDPOINTS.ADMIN.COLLECTION_GET('ImageAssets', newAssetId), {
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            credentials: "include",
            cache: "no-store"
          });

          if (res.ok) {
            const rawText = await res.text();
            const trimmed = rawText ? rawText.trim() : '';
            if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return;
            let json;
            try { json = JSON.parse(trimmed); } catch (e) { return; }
            const asset = json.data || json;
            if (asset && editor) {
              const assetUrl = asset.url || '';
              const assetTitle = asset.title || asset.altText || asset.fileName || '';
              const fileName = asset.fileName || 'Untitled';

              if (isVideoAssetEntry(asset)) {
                editor.chain().focus().insertContent({
                  type: 'videoAsset',
                  attrs: {
                    src: assetUrl,
                    title: assetTitle,
                    poster: asset.poster || '',
                    'data-filename': fileName,
                  },
                }).run();
              } else {
                editor.chain().focus().setImage({
                  src: assetUrl,
                  alt: assetTitle,
                  title: assetTitle,
                  'data-filename': fileName,
                }).run();
              }
            }
          }
        } catch (error) {
          console.error('Error fetching new asset:', error);
        }
      };
      
      fetchAndAddAsset();
      // Clean up URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, isBlogContent, navigate, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editor || !isBlogContent) return;

    const applyQuoteAuthorLayout = () => {
      const root = editor.view && editor.view.dom ? editor.view.dom : null;
      if (!root) return;

      root.querySelectorAll('blockquote').forEach((blockquote) => {
        const paragraphs = Array.from(blockquote.querySelectorAll('p'));
        const lastLine = [...paragraphs].reverse().find((p) => p.textContent && p.textContent.trim());
        if (!lastLine) return;

        const parsed = extractQuoteAndAuthor(lastLine.textContent || '');
        const existingAuthor = blockquote.querySelector('.tiptap-quote-author');

        if (!parsed.author) {
          blockquote.classList.remove('tiptap-motivational-quote');
          blockquote.removeAttribute('data-quote-author');
          if (existingAuthor) existingAuthor.remove();
          return;
        }

        blockquote.classList.add('tiptap-motivational-quote');
        blockquote.setAttribute('data-quote-author', parsed.author);

        if (lastLine.textContent !== parsed.quoteText) {
          lastLine.textContent = parsed.quoteText;
        }

        if (!existingAuthor) {
          const authorNode = document.createElement('div');
          authorNode.className = 'tiptap-quote-author';
          authorNode.textContent = parsed.author;
          blockquote.appendChild(authorNode);
        } else {
          existingAuthor.textContent = parsed.author;
        }
      });
    };

    applyQuoteAuthorLayout();
    editor.on('update', applyQuoteAuthorLayout);
    editor.on('selectionUpdate', applyQuoteAuthorLayout);

    return () => {
      editor.off('update', applyQuoteAuthorLayout);
      editor.off('selectionUpdate', applyQuoteAuthorLayout);
    };
  }, [editor, isBlogContent]);

  if (!editor) {
    return null;
  }

  const handleAddImage = () => {
    if (isBlogContent) {
      // Show dialog for BlogContent
      setShowImageDialog(true);
    } else {
      // Use prompt for other collections
    const url = prompt('Enter image URL:');
    if (!url) return;
    
    const title = prompt('Enter image title/text to display:');
    const imageTitle = title || '';
    
    if (VIDEO_ASSET_REGEX.test(url)) {
      editor.chain().focus().insertContent({
        type: 'videoAsset',
        attrs: { src: url, title: imageTitle, poster: '', 'data-filename': imageTitle || 'video' }
      }).run();
    } else {
      editor.chain().focus().setImage({
        src: url,
        alt: imageTitle,
        title: imageTitle
      }).run();
    }
    }
  };

  const handleSelectImages = (assets) => {
    if (!editor || !assets || assets.length === 0) return;
    
    // Insert each selected image/video
    assets.forEach((asset) => {
      const assetUrl = asset.url || '';
      const assetTitle = asset.title || asset.altText || asset.fileName || '';
      const fileName = asset.fileName || 'Untitled';

      if (isVideoAssetEntry(asset)) {
        editor.chain().focus().insertContent({
          type: 'videoAsset',
          attrs: {
            src: assetUrl,
            title: assetTitle,
            poster: asset.poster || '',
            'data-filename': fileName,
          },
        }).run();
      } else {
        editor.chain().focus().setImage({
          src: assetUrl,
          alt: assetTitle,
          title: assetTitle,
          'data-filename': fileName,
        }).run();
      }
    });
  };

  const handleAddLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleAddTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const handleAddEmbed = () => {
    const url = prompt('Enter embed URL (YouTube, Vimeo, etc.):');
    if (url) {
      // For now, we'll insert as a link. You can extend this with an embed extension if needed
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleAddTwitter = () => {
    const url = prompt('Enter Twitter Post URL:');
    if (url) {
      editor.chain().focus().insertContent({
        type: 'twitterEmbed',
        attrs: { url }
      }).run();
    }
  };

  const handleAddInstagram = () => {
    const url = prompt('Enter Instagram Post URL:');
    if (url) {
      editor.chain().focus().insertContent({
        type: 'instagramEmbed',
        attrs: { url }
      }).run();
    }
  };

  const handleAddYouTube = () => {
    const url = prompt('Enter YouTube Video URL or ID:');

    if (url) {
       let videoId = url;
        try {
            if (url.includes('youtube.com') || url.includes('youtu.be')) {
                const urlObj = new URL(url);
                if (urlObj.hostname.includes('youtu.be')) {
                    videoId = urlObj.pathname.slice(1);
                } else {
                    videoId = urlObj.searchParams.get('v');
                }
            }
        } catch (e) {
            console.error('Error parsing YouTube URL:', e);
        }

      editor.chain().focus().insertContent({
        type: 'youtubeEmbed',
        attrs: { videoId }
      }).run();
    }
  };

  const handleAddBlog = () => {
    // Get selected text
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, ' ');
    
    if (!text) {
        alert("Please select the text you want to use as the blog title first.");
        return;
    }

    const url = prompt('Enter Blog URL:');
    if (!url) return;

    // Use selected text as title
    const title = text;
    
    // Delete the selection first to replace it with the embed (since it's a block)
    // Or we can just let insertContent replace the selection
    editor.chain().focus().insertContent({
        type: 'blogEmbed',
        attrs: { url, title, image: '' }
    }).run();
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddBlogLink = () => {
    // Get selected text
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, ' ');
    
    if (!text) {
        alert("Please select the text you want to use as the blog link text first.");
        return;
    }

    const url = prompt('Enter Blog URL:');
    if (!url) return;

    editor.chain().focus().insertContent({
      type: 'blogLink',
      attrs: { url, text }
    }).run();
  };

  const getCurrentHeading = () => {
    if (editor.isActive('heading', { level: 1 })) return 'Heading 1';
    if (editor.isActive('heading', { level: 2 })) return 'Heading 2';
    if (editor.isActive('heading', { level: 3 })) return 'Heading 3';
    if (editor.isActive('heading', { level: 4 })) return 'Heading 4';
    if (editor.isActive('heading', { level: 5 })) return 'Heading 5';
    if (editor.isActive('heading', { level: 6 })) return 'Heading 6';
    return 'Normal text';
  };

  const handleHeadingChange = (level) => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  return (
    <div className={`tiptap-editor-container${isFullscreen ? ' tiptap-fullscreen' : ''}${navPinned ? ' nav-pinned' : ''}`}>
      {/* Toolbar */}
      <div className="admin-tiptap-toolbar">
        {/* Text Style Dropdown */}
        <select
          value={getCurrentHeading()}
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'Normal text') {
              handleHeadingChange(0);
            } else {
              const level = parseInt(value.replace('Heading ', ''));
              handleHeadingChange(level);
            }
          }}          className="admin-tiptap-heading-select"
        >
          <option value="Normal text">Normal text</option>
          <option value="Heading 1">Heading 1</option>
          <option value="Heading 2">Heading 2</option>
          <option value="Heading 3">Heading 3</option>
          <option value="Heading 4">Heading 4</option>
          <option value="Heading 5">Heading 5</option>
          <option value="Heading 6">Heading 6</option>
        </select>

        {/* Basic Formatting */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          type="button"
          title="Bold"
          className={`admin-tiptap-btn${editor.isActive('bold') ? ' active' : ''}`}
        >
          <i className="fi fi-br-bold"></i>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          type="button"
          title="Italic"
          className={`admin-tiptap-btn${editor.isActive('italic') ? ' active' : ''}`}
        >
          <i className="fi fi-br-italic"></i>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          type="button"
          title="Underline"
          className={`admin-tiptap-btn${editor.isActive('underline') ? ' active' : ''}`}
        >
          <i className="fi fi-br-underline"></i>
        </button>

        {/* Highlight */}
        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          type="button"
          title="Highlight"
          className={`admin-tiptap-btn${editor.isActive('highlight') ? ' active' : ''}`}
        >
          <i className="fi fi-ss-highlighter-line"></i>
        </button>

        {/* Vertical Separator after Highlight */}
        <div className="admin-tiptap-separator"></div>

        {/* Link and Quote */}
        <button
          onClick={handleAddLink}
          type="button"
          title="Link"
          className={`admin-tiptap-btn${editor.isActive('link') ? ' active' : ''}`}
        >
          <i className="fi fi-br-link-alt"></i>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          type="button"
          title="Quote"
          className={`admin-tiptap-btn${editor.isActive('blockquote') ? ' active' : ''}`}
        >
          <i className="fi fi-br-quote-right"></i>
        </button>

        {/* Code and Code Block */}
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          type="button"
          title="Code"
          className={`admin-tiptap-btn${editor.isActive('code') ? ' active' : ''}`}
        >
          <i className="fi fi-br-code-simple"></i>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          type="button"
          title="Code Block"
          className={`admin-tiptap-btn${editor.isActive('codeBlock') ? ' active' : ''}`}
        >
          <i className="fi fi-rr-square-code"></i>
        </button>

        {/* Vertical Separator before Twitter */}
        <div className="admin-tiptap-separator"></div>

        <button
          onClick={handleAddInstagram}
          type="button"
          title="Instagram Post"
          className={`admin-tiptap-btn${editor.isActive('instagramEmbed') ? ' active' : ''}`}
        >
          <i className="fi fi-brands-instagram"></i>
        </button>
        <button
          onClick={handleAddTwitter}
          type="button"
          title="Twitter Post"
          className={`admin-tiptap-btn${editor.isActive('twitterEmbed') ? ' active' : ''}`}
        >
          <i className="fi fi-brands-twitter"></i>
        </button>
        <button
          onClick={handleAddYouTube}
          type="button"
          title="YouTube Video"
          className={`admin-tiptap-btn${editor.isActive('youtubeEmbed') ? ' active' : ''}`}
        >
          <i className="fi fi-brands-youtube"></i>
        </button>
        <button
          onClick={handleAddBlog}
          type="button"
          title="Add Blog Post"
          className={`admin-tiptap-btn${editor.isActive('blogEmbed') ? ' active' : ''}`}
        >
          <i className="fi fi-sr-journal-alt"></i>
        </button>
        <button
          onClick={() => {
            // Get selected text
            const { from, to } = editor.state.selection;
            const text = editor.state.doc.textBetween(from, to, ' ');
            
            if (!text) {
                alert("Please select some text first to turn into a podcast link.");
                return;
            }

            const url = prompt('Enter Podcast URL:');
            if (url) {
                editor.chain().focus().deleteSelection().insertContent({
                    type: 'podcastLink',
                    attrs: { url, text }
                }).run();
            }
          }}
          type="button"
          title="Add My Podcast"
          className={`admin-tiptap-btn${editor.isActive('podcastLink') ? ' active' : ''}`}
        >
          <i className="fi fi-sr-podcast"></i>
        </button>

        {/* Vertical Separator after Code Block */}
        <div className="admin-tiptap-separator"></div>

        <button
          onClick={handleAddImage}
          type="button"
          title="Image"
          className="admin-tiptap-btn"
        >
          <i className="fi fi-rr-images"></i>
        </button>

        {/* Lists */}
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          type="button"
          title="Bullet List"
          className={`admin-tiptap-btn${editor.isActive('bulletList') ? ' active' : ''}`}
        >
          <i className="fi fi-rr-list"></i>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          type="button"
          title="Numbered List"
          className={`admin-tiptap-btn${editor.isActive('orderedList') ? ' active' : ''}`}
        >
          <i className="fi fi-rr-input-numeric"></i>
        </button>

        {/* Table */}
        <button
          onClick={handleAddTable}
          type="button"
          title="Table"
          className={`admin-tiptap-btn${editor.isActive('table') ? ' active' : ''}`}
        >
          <i className="fi fi-rr-table-list"></i>
        </button>

        {/* Subscript and Superscript */}
        <button
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          type="button"
          title="Subscript"
          className={`admin-tiptap-btn${editor.isActive('subscript') ? ' active' : ''}`}
        >
          <i className="fi fi-br-subscript"></i>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          type="button"
          title="Superscript"
          className={`admin-tiptap-btn${editor.isActive('superscript') ? ' active' : ''}`}
        >
          <i className="fi fi-br-superscript"></i>
        </button>

        {/* Embed */}
        <button
          onClick={handleAddEmbed}
          type="button"
          title="Embed"
          className="admin-tiptap-btn admin-tiptap-btn-embed"
        >
          Embed
        </button>

        {/* Fullscreen - moved to right end */}
        <div className="admin-tiptap-toolbar-end">
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            type="button"
            title="Fullscreen"
            className={`admin-tiptap-btn${isFullscreen ? ' active' : ''}`}
          >
            <i className="fi fi-br-expand"></i>
          </button>
        </div>
      </div>

      {/* Image Asset Dialog - Always render but only show for BlogContent */}
      <ImageAssetDialog
        isOpen={isBlogContent && showImageDialog}
        onClose={() => setShowImageDialog(false)}
        onSelectImages={handleSelectImages}
        editor={editor}
        navigate={navigate}
        location={location}
        onBeforeNavigate={onBeforeNavigate}
      />

      {/* Editor */}
      <div className={isFullscreen ? 'tiptap-editor-fullscreen' : 'tiptap-editor-normal'}>
        {/* ImageProcessor disabled to prevent infinite loops - images will display normally */}
        {/* <ImageProcessor editor={editor} /> */}
        <EditorContent editor={editor} />
      </div>

    </div>
  );
}

